import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface JuniorNavState {
  animateEntry?: boolean;
  [key: string]: unknown;
}

const EXIT_MS = 220;

interface JuniorAliveNavContextValue {
  animateEntry: boolean;
  navigateAlive: (to: string, opts?: { replace?: boolean; state?: JuniorNavState }) => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

const JuniorAliveNavContext = createContext<JuniorAliveNavContextValue | null>(null);

function useJuniorAliveNavInternal(): JuniorAliveNavContextValue {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  const animateEntry = Boolean((location.state as JuniorNavState | null)?.animateEntry);

  useEffect(() => {
    if (!animateEntry) return;
    const st = (location.state as JuniorNavState | null) ?? {};
    const { animateEntry: _drop, ...rest } = st;
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: Object.keys(rest).length > 0 ? rest : undefined }
    );
  }, [animateEntry, location.pathname, location.search, location.hash, navigate]);

  const navigateAlive = useCallback(
    (to: string, opts?: { replace?: boolean; state?: JuniorNavState }) => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const el = containerRef.current ?? document.querySelector<HTMLElement>('.junior-alive-page');

      const go = () => {
        navigate(to, {
          replace: opts?.replace,
          state: reducedMotion ? opts?.state : { animateEntry: true, ...opts?.state },
        });
      };

      if (el && !reducedMotion) {
        el.classList.add('junior-alive-exit');
        window.setTimeout(go, EXIT_MS);
      } else {
        go();
      }
    },
    [navigate]
  );

  return useMemo(
    () => ({ animateEntry, navigateAlive, containerRef }),
    [animateEntry, navigateAlive]
  );
}

export function JuniorAliveNavProvider({ children }: { children: React.ReactNode }) {
  const value = useJuniorAliveNavInternal();
  return <JuniorAliveNavContext.Provider value={value}>{children}</JuniorAliveNavContext.Provider>;
}

export function useJuniorAliveNav(): JuniorAliveNavContextValue {
  const ctx = useContext(JuniorAliveNavContext);
  if (ctx) return ctx;
  return useJuniorAliveNavInternal();
}
